import React from 'react';
import Icon from '../../../components/AppIcon';

const PaymentMethodPerformance = ({ performanceData, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!performanceData || performanceData?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Payment Method Performance</h3>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Icon name="CreditCard" size={48} className="mb-2" />
          <p>No payment method data available</p>
        </div>
      </div>
    );
  }

  const getMethodIcon = (method) => {
    const icons = {
      credit_card: 'CreditCard',
      debit_card: 'CreditCard',
      bank_transfer: 'Building2',
      paypal: 'Wallet',
      stripe: 'Zap',
      cash: 'Banknote',
    };
    return icons?.[method?.toLowerCase()] || 'CreditCard';
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return 'text-success';
    if (rate >= 85) return 'text-warning';
    return 'text-destructive';
  };

  const totalTransactions = performanceData?.reduce((sum, d) => sum + (d?.total_transactions || 0), 0);
  const totalRevenue = performanceData?.reduce((sum, d) => sum + (d?.total_revenue || 0), 0);

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Payment Method Performance</h3>
          <p className="text-sm text-muted-foreground">Success rates and transaction volumes by method</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Total Transactions</p>
          <p className="text-xl font-bold text-foreground">{totalTransactions}</p>
        </div>
      </div>

      <div className="space-y-4">
        {performanceData?.map((method, index) => {
          const successRate = (method?.successful_transactions / method?.total_transactions) * 100;
          const revenuePercentage = (method?.total_revenue / totalRevenue) * 100;

          return (
            <div key={index} className="bg-accent/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name={getMethodIcon(method?.payment_method)} className="text-primary" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground capitalize">
                      {method?.payment_method?.replace('_', ' ')}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {method?.total_transactions} transactions
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-foreground">
                    ${method?.total_revenue?.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {revenuePercentage?.toFixed(1)}% of total
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Success Rate</p>
                  <p className={`text-sm font-semibold ${getSuccessRateColor(successRate)}`}>
                    {successRate?.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Avg Amount</p>
                  <p className="text-sm font-semibold text-foreground">
                    ${method?.average_transaction_amount?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Failed</p>
                  <p className="text-sm font-semibold text-destructive">
                    {method?.failed_transactions}
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-success rounded-full transition-all"
                    style={{ width: `${successRate}%` }}
                  ></div>
                </div>
              </div>

              {method?.recent_issues && method?.recent_issues?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" className="text-warning mt-0.5" size={14} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-foreground mb-1">Recent Issues</p>
                      <ul className="space-y-1">
                        {method?.recent_issues?.map((issue, idx) => (
                          <li key={idx} className="text-xs text-muted-foreground">
                            • {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-success/10 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="CheckCircle" className="text-success" size={16} />
              <p className="text-sm font-medium text-foreground">Overall Success Rate</p>
            </div>
            <p className="text-2xl font-bold text-success">
              {
                (
                  (performanceData?.reduce((sum, d) => sum + (d?.successful_transactions || 0), 0) /
                    totalTransactions) *
                  100
                )?.toFixed(1)
              }%
            </p>
          </div>
          <div className="bg-primary/10 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="DollarSign" className="text-primary" size={16} />
              <p className="text-sm font-medium text-foreground">Total Revenue</p>
            </div>
            <p className="text-2xl font-bold text-primary">${totalRevenue?.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodPerformance;